<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
const open = ref(false);
const busy = ref(false);
const error = ref('');
const toggle = ref<globalThis.HTMLButtonElement | null>(null);
function close(): void {
  open.value = false;
  toggle.value?.focus();
}
function revalidate(event: globalThis.PageTransitionEvent): void {
  if (event.persisted) globalThis.location.reload();
}
onMounted(() => globalThis.addEventListener('pageshow', revalidate));
onBeforeUnmount(() => globalThis.removeEventListener('pageshow', revalidate));
async function signOut(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    // Cookies are restricted to each profile path. Revoke both without losing
    // the second cookie before its corresponding server session is revoked.
    for (const role of ['admin', 'provider']) {
      const response = await globalThis.fetch(`/${role}/api/operator/logout`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!response.ok) throw new Error('LOGOUT_FAILED');
    }
    globalThis.location.replace('/');
  } catch {
    error.value = 'Déconnexion incomplète. Vérifiez votre connexion puis réessayez.';
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <div class="operator-account" @keydown.esc.stop.prevent="close">
    <button
      ref="toggle"
      type="button"
      class="operator-account-toggle"
      aria-label="Compte : changer de compte ou se déconnecter"
      :aria-expanded="open"
      aria-controls="operator-account-panel"
      @click="open = !open"
    >
      <span aria-hidden="true">O</span>
    </button>
    <div
      v-if="open"
      id="operator-account-panel"
      class="operator-account-panel"
      role="group"
      aria-label="Mon compte"
    >
      <button type="button" :disabled="busy" @click="signOut">Changer de compte</button>
      <button type="button" :disabled="busy" @click="signOut">Se déconnecter</button>
      <button type="button" :disabled="busy" @click="close">Annuler</button>
      <p v-if="busy" role="status">Déconnexion…</p>
      <p v-if="error" role="alert">{{ error }}</p>
    </div>
  </div>
</template>
<style scoped>
.operator-account {
  position: relative;
  flex: 0 0 auto;
  letter-spacing: normal;
  text-transform: none;
}
.operator-account-toggle {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 3px solid #244d3d;
  border-radius: 11px;
  background: #fff;
  color: #244d3d;
  font: inherit;
  font-size: 19px;
  cursor: pointer;
}
.operator-account-toggle:focus-visible,
.operator-account-panel button:focus-visible {
  outline: 3px solid #d69a00;
  outline-offset: 3px;
}
.operator-account-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 100;
  width: min(280px, calc(100vw - 48px));
  padding: 10px;
  border: 1px solid #c6cdd5;
  border-radius: 14px;
  background: #fff;
  color: #172033;
  box-shadow: 0 8px 28px #17203330;
  font-size: 16px;
  line-height: 1.4;
}
.operator-account-panel button {
  display: block;
  width: 100%;
  min-height: 46px;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  background: #eef1f5;
  color: #172033;
  text-align: left;
  font: inherit;
  cursor: pointer;
}
.operator-account-panel button + button {
  margin-top: 8px;
}
.operator-account-panel button:disabled {
  opacity: 0.6;
  cursor: wait;
}
.operator-account-panel [role='alert'] {
  color: #a41728;
}
</style>
